/**
 * User profile page — account info, password change, MFA devices, sessions
 */
import { useState, useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { parseServerDate } from '../utils/formatDateTime'
import { useAuth } from '../services/auth'
import { qrCodeSvgDataUrl } from '../utils/qrCode'
import { updateProfile, changePassword } from '../services/users'
import {
  listMFADevices,
  enrollMFADevice,
  deleteMFADevice,
  renameMFADevice,
  rotateBackupCodes,
  listSessions,
  logoutAllOtherSessions,
  logoutSession,
} from '../services/mfa'
import {
  listPasskeys,
  enrollPasskey,
  deletePasskey,
  renamePasskey,
  isPasskeySupported,
} from '../services/passkeys'
import type { MFADevice, MFAEnrollResponse, ReauthData, UserSession, Passkey, UserNotificationAddress, UserNotificationPref, TrustedDevice, LoginPolicy, ActivityEntry } from '../types'
import { listMyTrustedDevices, revokeMyTrustedDevice, getMyLoginPolicy, updateMyLoginPolicy } from '../services/security'
import { getMyActivity } from '../services/users'
import Modal, { ModalFooter } from '../components/Modal'
import { PasswordStrengthBar } from '../components/PasswordStrengthBar'
import InfoHint from '../components/InfoHint'
import PasskeyReauthButton from '../components/PasskeyReauthButton'
import {
  listNotificationAddresses,
  addNotificationAddress,
  deleteNotificationAddress,
  listNotificationPrefs,
  updateNotificationPref,
} from '../services/notifications'
import { formatCidrLines, parseCidrLines } from '../utils/cidrLines'

// ===== Re-auth modal (shared by remove device and rotate backup codes) =====

interface ReauthModalProps {
  title: string
  description?: string
  confirmLabel: string
  onConfirm: (data: ReauthData) => Promise<void>
  onClose: () => void
}

function ReauthModal({ title, description, confirmLabel, onConfirm, onClose }: ReauthModalProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      await onConfirm({ current_password: currentPassword, mfa_code: mfaCode || undefined })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    } finally {
      setSaving(false)
    }
  }

  // Passkey path: the assertion supplies the second factor in place of a code,
  // so submit the operation with the current password + the passkey token.
  async function handlePasskeyToken(token: string) {
    setSaving(true)
    try {
      await onConfirm({ current_password: currentPassword, reauth_token: token })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal titleId="reauth-modal-title" title={title} subtitle={description} onClose={onClose} maxWidth="max-w-sm">
      {error && <div className="alert alert-error mb-4" role="alert">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="label">Current Password</label>
          <input
            type="password"
            className="input"
            required
            autoFocus
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="label inline-flex items-center gap-1.5">
            Authenticator or backup code
            <InfoHint label="What code is this?">
              The 6-digit code from your authenticator app, or one of your one-time
              backup codes. Leave this blank if you'll verify with a passkey instead.
            </InfoHint>
          </label>
          <input
            type="text"
            inputMode="text"
            maxLength={8}
            className="input"
            placeholder="6-digit or backup code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
          />
        </div>
        <ModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={saving || !currentPassword} className="btn btn-primary">
            {saving ? 'Verifying…' : confirmLabel}
          </button>
        </ModalFooter>
      </form>
      <PasskeyReauthButton
        onToken={handlePasskeyToken}
        onError={setError}
        disabled={saving || !currentPassword}
      />
    </Modal>
  )
}

// ===== Add Authenticator modal =====

interface AddAuthModalProps {
  onClose: () => void
  onEnrolled: () => void
}

function AddAuthModal({ onClose, onEnrolled }: AddAuthModalProps) {
  const [step, setStep] = useState<'form' | 'qr'>('form')
  const [deviceLabel, setDeviceLabel] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [enrollResult, setEnrollResult] = useState<MFAEnrollResponse | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showBackupCodes, setShowBackupCodes] = useState(false)

  useEffect(() => {
    if (enrollResult?.qr_uri) {
      qrCodeSvgDataUrl(enrollResult.qr_uri).then(setQrDataUrl).catch(() => {})
    }
  }, [enrollResult?.qr_uri])

  async function doEnroll(reauthToken?: string) {
    setError('')
    setSaving(true)
    try {
      const result = await enrollMFADevice({
        device_label: deviceLabel,
        current_password: currentPassword,
        mfa_code: mfaCode || undefined,
        reauth_token: reauthToken || undefined,
      })
      setEnrollResult(result)
      setStep('qr')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrollment failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleEnroll(e: React.FormEvent) {
    e.preventDefault()
    await doEnroll()
  }

  if (step === 'qr' && enrollResult) {
    return (
      <Modal
        titleId="add-auth-qr-title"
        title="Scan QR Code"
        subtitle="Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.), then tap Done."
        onClose={onClose}
        maxWidth="max-w-md"
      >
        {qrDataUrl && (
          <div className="flex justify-center mb-4">
            <img src={qrDataUrl} alt="MFA QR code" className="border rounded p-2" />
          </div>
        )}
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-1">Or enter this secret manually:</p>
          <code className="block bg-gray-100 px-2 py-1 rounded text-xs break-all">
            {enrollResult.secret}
          </code>
        </div>
        <div className="mb-4">
          <button
            type="button"
            className="tap text-sm text-primary-600 hover:underline"
            onClick={() => setShowBackupCodes(!showBackupCodes)}
            aria-expanded={showBackupCodes}
          >
            {showBackupCodes ? 'Hide' : 'Show'} backup codes ({enrollResult.backup_codes.length})
          </button>
          {showBackupCodes && (
            <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-xs text-yellow-700 mb-2 font-medium">
                Save these codes securely — they will not be shown again.
              </p>
              <div className="grid grid-cols-2 gap-1">
                {enrollResult.backup_codes.map((code) => (
                  <code key={code} className="text-xs font-mono bg-white px-1 py-0.5 rounded border">
                    {code}
                  </code>
                ))}
              </div>
            </div>
          )}
        </div>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              onEnrolled()
              onClose()
            }}
          >
            Done
          </button>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Modal
      titleId="add-auth-title"
      title="Add Authenticator"
      subtitle="Give this device a name and confirm your identity."
      onClose={onClose}
      maxWidth="max-w-sm"
    >
      {error && <div className="alert alert-error mb-4" role="alert">{error}</div>}
      <form onSubmit={handleEnroll} className="space-y-3">
        <div>
          <label className="label">Device Label</label>
          <input
            type="text"
            className="input"
            required
            autoFocus
            maxLength={50}
            placeholder="e.g. Phone, Tablet, Work phone"
            value={deviceLabel}
            onChange={(e) => setDeviceLabel(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Current Password</label>
          <input
            type="password"
            className="input"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="label inline-flex items-center gap-1.5">
            Authenticator or backup code (if you already have a factor)
            <InfoHint label="What code is this?">
              If you've already set up an authenticator app or a passkey, confirm
              it's you: enter a current code, or verify with a passkey below. Leave
              blank if this is your very first factor.
            </InfoHint>
          </label>
          <input
            type="text"
            inputMode="text"
            maxLength={8}
            className="input"
            placeholder="6-digit or backup code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
          />
        </div>
        <ModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !deviceLabel || !currentPassword}
            className="btn btn-primary"
          >
            {saving ? 'Setting up…' : 'Continue'}
          </button>
        </ModalFooter>
      </form>
      <PasskeyReauthButton
        onToken={doEnroll}
        onError={setError}
        disabled={saving || !deviceLabel || !currentPassword}
      />
    </Modal>
  )
}

// ===== New Backup Codes display (after rotation) =====

interface BackupCodesDisplayProps {
  codes: string[]
  onClose: () => void
}

function BackupCodesDisplay({ codes, onClose }: BackupCodesDisplayProps) {
  return (
    <Modal titleId="backup-codes-title" title="New Backup Codes" onClose={onClose} maxWidth="max-w-md">
      <div className="alert alert-warning mb-4 text-sm">
        Save these codes somewhere secure. They will not be shown again — your old codes are now
        invalid.
      </div>
      <div className="grid grid-cols-2 gap-1 mb-4">
        {codes.map((code) => (
          <code key={code} className="text-sm font-mono bg-gray-100 px-2 py-1 rounded border">
            {code}
          </code>
        ))}
      </div>
      <ModalFooter>
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      </ModalFooter>
    </Modal>
  )
}

// ===== Add Passkey modal =====

interface AddPasskeyModalProps {
  onClose: () => void
  onEnrolled: () => void
}

function AddPasskeyModal({ onClose, onEnrolled }: AddPasskeyModalProps) {
  const [label, setLabel] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function doEnroll(reauthToken?: string) {
    setError('')
    setSaving(true)
    try {
      await enrollPasskey(label, currentPassword, mfaCode || undefined, reauthToken)
      onEnrolled()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey enrollment failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    await doEnroll()
  }

  return (
    <Modal
      titleId="add-passkey-title"
      title="Add Passkey"
      subtitle="Give this passkey a name, then follow your device's prompt (fingerprint, Face ID, screen lock, or security key)."
      onClose={onClose}
      maxWidth="max-w-sm"
    >
      {error && <div className="alert alert-error mb-4" role="alert">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="label">Passkey Label</label>
          <input
            type="text"
            className="input"
            required
            autoFocus
            maxLength={50}
            placeholder="e.g. MacBook, iPhone, YubiKey"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Current Password</label>
          <input
            type="password"
            className="input"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="label inline-flex items-center gap-1.5">
            Authenticator or backup code (if enrolled)
            <InfoHint label="What code is this?">
              Confirm it's you before adding a passkey: enter a current
              authenticator or backup code, or verify with an existing passkey
              below. Leave blank if you have no other factor yet.
            </InfoHint>
          </label>
          <input
            type="text"
            inputMode="text"
            maxLength={8}
            className="input"
            placeholder="6-digit or backup code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
          />
        </div>
        <ModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !label || !currentPassword}
            className="btn btn-primary"
          >
            {saving ? 'Waiting for device…' : 'Add Passkey'}
          </button>
        </ModalFooter>
      </form>
      <PasskeyReauthButton
        onToken={doEnroll}
        onError={setError}
        disabled={saving || !label || !currentPassword}
        label="Verify with an existing passkey"
      />
    </Modal>
  )
}

// ===== Main ProfilePage =====

export default function ProfilePage() {
  const { user, refreshUser } = useAuth()

  // Profile edit
  const [editName, setEditName] = useState(user?.full_name ?? '')
  const [editPhone, setEditPhone] = useState(user?.phone ?? '')
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileError, setProfileError] = useState('')
  const [profileSuccess, setProfileSuccess] = useState(false)

  // Change password
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [passwordMfaCode, setPasswordMfaCode] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  // MFA devices
  const [devices, setDevices] = useState<MFADevice[]>([])
  const [devicesLoading, setDevicesLoading] = useState(true)
  const [devicesError, setDevicesError] = useState('')
  const [showAddAuth, setShowAddAuth] = useState(false)
  const [removingDevice, setRemovingDevice] = useState<MFADevice | null>(null)
  const [showRotateCodes, setShowRotateCodes] = useState(false)
  const [newCodes, setNewCodes] = useState<string[] | null>(null)
  const [renamingDeviceId, setRenamingDeviceId] = useState<number | null>(null)
  const [renameDeviceLabel, setRenameDeviceLabel] = useState('')

  // Passkeys
  const [passkeys, setPasskeys] = useState<Passkey[]>([])
  const [passkeysLoading, setPasskeysLoading] = useState(true)
  const [passkeysError, setPasskeysError] = useState('')
  const [showAddPasskey, setShowAddPasskey] = useState(false)
  const [removingPasskey, setRemovingPasskey] = useState<Passkey | null>(null)
  const [renamingPasskeyId, setRenamingPasskeyId] = useState<number | null>(null)
  const [renamePasskeyLabel, setRenamePasskeyLabel] = useState('')

  // Sessions
  const [sessions, setSessions] = useState<UserSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [sessionsError, setSessionsError] = useState('')
  const [loggingOutAll, setLoggingOutAll] = useState(false)
  const [loggingOutSessionId, setLoggingOutSessionId] = useState<number | null>(null)
  const [sessionsSuccess, setSessionsSuccess] = useState('')

  // Trusted devices + login policy
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([])
  const [trustedDevicesLoading, setTrustedDevicesLoading] = useState(true)
  const [trustedDevicesError, setTrustedDevicesError] = useState('')
  const [revokingDeviceId, setRevokingDeviceId] = useState<number | null>(null)
  const [, setLoginPolicy] = useState<LoginPolicy>({ mode: 'open', allowed_cidrs: [] })
  const [policyLoading, setPolicyLoading] = useState(true)
  const [policyError, setPolicyError] = useState('')
  const [policySuccess, setPolicySuccess] = useState('')
  const [policyMode, setPolicyMode] = useState<'open' | 'ip_only' | 'ip_or_trusted_device'>('open')
  const [policyCidrs, setPolicyCidrs] = useState('')
  const [savingPolicy, setSavingPolicy] = useState(false)

  // Activity feed
  const [activity, setActivity] = useState<ActivityEntry[]>([])
  const [activityLoading, setActivityLoading] = useState(true)
  const [activityError, setActivityError] = useState('')

  // Notification settings
  const [notifAddresses, setNotifAddresses] = useState<UserNotificationAddress[]>([])
  const [notifPrefs, setNotifPrefs] = useState<UserNotificationPref[]>([])
  const [notifLoading, setNotifLoading] = useState(true)
  const [notifError, setNotifError] = useState('')
  const [showAddAddress, setShowAddAddress] = useState(false)
  const [addAddrChannel, setAddAddrChannel] = useState('email')
  const [addAddrAddress, setAddAddrAddress] = useState('')
  const [addAddrLabel, setAddAddrLabel] = useState('')
  const [addAddrSubmitting, setAddAddrSubmitting] = useState(false)
  const [addAddrError, setAddAddrError] = useState('')
  const [deletingAddrId, setDeletingAddrId] = useState<number | null>(null)
  const [updatingPrefPriority, setUpdatingPrefPriority] = useState<string | null>(null)

  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // When a password change is mandatory the backend 403s every panel endpoint
    // (only /users/me, /auth/session, logout, and the password change are
    // allowed). Skip those loads so we don't flood the console with 403s — the
    // focused change-password view below is all that renders anyway.
    if (!user?.force_password_change) {
      loadDevices()
      loadSessions()
      loadPasskeys()
      loadNotifSettings()
      loadTrustedDevices()
      loadLoginPolicy()
      loadActivity()
    }
    return () => {
      const timer = successTimerRef.current // eslint-disable-line react-hooks/exhaustive-deps
      if (timer) clearTimeout(timer)
    }
    // Mount-only on purpose. `user?.force_password_change` is read above but is
    // deliberately NOT a dependency: these are one-shot panel loads, and re-running
    // them when the flag flips would re-fetch every panel the moment the user
    // finishes a mandatory password change. The flag is settled before this page
    // renders (the route is behind the auth gate), so there is nothing to react to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function loadPasskeys() {
    setPasskeysLoading(true)
    setPasskeysError('')
    try {
      const data = await listPasskeys()
      setPasskeys(data)
    } catch (err) {
      setPasskeysError(err instanceof Error ? err.message : 'Failed to load passkeys')
    } finally {
      setPasskeysLoading(false)
    }
  }

  async function handleRemovePasskey(data: ReauthData) {
    if (!removingPasskey) return
    await deletePasskey(removingPasskey.id, data.current_password, data.mfa_code, data.reauth_token)
    setRemovingPasskey(null)
    await loadPasskeys()
  }

  async function loadDevices() {
    setDevicesLoading(true)
    setDevicesError('')
    try {
      const data = await listMFADevices()
      setDevices(data)
    } catch (err) {
      setDevicesError(err instanceof Error ? err.message : 'Failed to load MFA devices')
    } finally {
      setDevicesLoading(false)
    }
  }

  async function loadSessions() {
    setSessionsLoading(true)
    setSessionsError('')
    try {
      const data = await listSessions()
      setSessions(data)
    } catch (err) {
      setSessionsError(err instanceof Error ? err.message : 'Failed to load sessions')
    } finally {
      setSessionsLoading(false)
    }
  }

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault()
    setProfileError('')
    setProfileSuccess(false)
    setSavingProfile(true)
    try {
      await updateProfile({ full_name: editName, phone: editPhone })
      await refreshUser()
      setProfileSuccess(true)
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to save profile')
    } finally {
      setSavingProfile(false)
    }
  }

  // reauthToken (from a passkey assertion) supplies the second factor in place
  // of an authenticator/backup code — a passkey-only user has no code to type.
  async function doChangePassword(reauthToken?: string) {
    setPasswordError('')
    setPasswordSuccess(false)

    if (newPassword.length < 15) {
      setPasswordError('New password must be at least 15 characters.')
      return
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordError('Passwords do not match.')
      return
    }

    setSavingPassword(true)
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        mfa_code: passwordMfaCode || undefined,
        reauth_token: reauthToken || undefined,
      })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmNewPassword('')
      setPasswordMfaCode('')
      setPasswordSuccess(true)
      // The backend clears force_password_change on a successful change; refresh
      // the cached user so a mandatory-change session leaves the focused view and
      // the full profile page returns without a manual reload.
      await refreshUser()
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password')
    } finally {
      setSavingPassword(false)
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault()
    await doChangePassword()
  }

  async function handleRemoveDevice(data: ReauthData) {
    if (!removingDevice) return
    await deleteMFADevice(removingDevice.id, data)
    setRemovingDevice(null)
    await loadDevices()
  }

  async function handleRotateCodes(data: ReauthData) {
    const codes = await rotateBackupCodes(data)
    setShowRotateCodes(false)
    setNewCodes(codes)
    await loadDevices()
  }

  async function handleLogoutSession(sessionId: number) {
    setLoggingOutSessionId(sessionId)
    setSessionsSuccess('')
    setSessionsError('')
    try {
      await logoutSession(sessionId)
      setSessions((prev) => prev.filter((s) => s.id !== sessionId))
    } catch (err) {
      setSessionsError(err instanceof Error ? err.message : 'Failed to log out session')
    } finally {
      setLoggingOutSessionId(null)
    }
  }

  async function handleLogoutAll() {
    setLoggingOutAll(true)
    setSessionsSuccess('')
    setSessionsError('')
    try {
      await logoutAllOtherSessions()
      setSessionsSuccess('All other sessions have been logged out.')
      await loadSessions()
    } catch (err) {
      setSessionsError(err instanceof Error ? err.message : 'Failed to log out sessions')
    } finally {
      setLoggingOutAll(false)
    }
  }

  async function loadTrustedDevices() {
    setTrustedDevicesLoading(true)
    setTrustedDevicesError('')
    try {
      setTrustedDevices(await listMyTrustedDevices())
    } catch (err) {
      setTrustedDevicesError(err instanceof Error ? err.message : 'Failed to load trusted devices')
    } finally {
      setTrustedDevicesLoading(false)
    }
  }

  async function handleRevokeDevice(id: number) {
    setRevokingDeviceId(id)
    try {
      await revokeMyTrustedDevice(id)
      setTrustedDevices((prev) => prev.filter((d) => d.id !== id))
    } catch {
      setTrustedDevicesError('Failed to revoke device')
    } finally {
      setRevokingDeviceId(null)
    }
  }

  async function loadLoginPolicy() {
    setPolicyLoading(true)
    setPolicyError('')
    try {
      const p = await getMyLoginPolicy()
      setLoginPolicy(p)
      setPolicyMode(p.mode)
      setPolicyCidrs(formatCidrLines(p.allowed_cidrs, p.cidr_labels))
    } catch (err) {
      setPolicyError(err instanceof Error ? err.message : 'Failed to load login policy')
    } finally {
      setPolicyLoading(false)
    }
  }

  async function handleSavePolicy() {
    setSavingPolicy(true)
    setPolicyError('')
    setPolicySuccess('')
    const { cidrs, labels } = parseCidrLines(policyCidrs)
    try {
      const updated = await updateMyLoginPolicy({
        mode: policyMode,
        allowed_cidrs: cidrs,
        cidr_labels: labels,
      })
      setLoginPolicy(updated)
      setPolicyCidrs(formatCidrLines(updated.allowed_cidrs, updated.cidr_labels))
      setPolicySuccess('Login policy saved.')
    } catch (err) {
      setPolicyError(err instanceof Error ? err.message : 'Failed to save login policy')
    } finally {
      setSavingPolicy(false)
    }
  }

  async function loadActivity() {
    setActivityLoading(true)
    setActivityError('')
    try {
      setActivity(await getMyActivity(20))
    } catch (err) {
      setActivityError(err instanceof Error ? err.message : 'Failed to load activity')
    } finally {
      setActivityLoading(false)
    }
  }

  async function loadNotifSettings() {
    setNotifLoading(true)
    setNotifError('')
    try {
      const [addrs, prefs] = await Promise.all([
        listNotificationAddresses(),
        listNotificationPrefs(),
      ])
      setNotifAddresses(addrs)
      setNotifPrefs(prefs)
    } catch (err) {
      setNotifError(err instanceof Error ? err.message : 'Failed to load notification settings')
    } finally {
      setNotifLoading(false)
    }
  }

  async function handleAddAddress() {
    if (!addAddrAddress.trim() || !addAddrLabel.trim()) return
    setAddAddrSubmitting(true)
    setAddAddrError('')
    try {
      await addNotificationAddress({
        channel: addAddrChannel,
        address: addAddrAddress.trim(),
        label: addAddrLabel.trim(),
      })
      setShowAddAddress(false)
      setAddAddrAddress('')
      setAddAddrLabel('')
      setAddAddrChannel('email')
      await loadNotifSettings()
    } catch (err) {
      setAddAddrError(err instanceof Error ? err.message : 'Failed to add address')
    } finally {
      setAddAddrSubmitting(false)
    }
  }

  async function handleDeleteAddress(id: number) {
    setDeletingAddrId(id)
    try {
      await deleteNotificationAddress(id)
      setNotifAddresses((prev) => prev.filter((a) => a.id !== id))
    } catch (err) {
      setNotifError(err instanceof Error ? err.message : 'Failed to remove address')
    } finally {
      setDeletingAddrId(null)
    }
  }

  async function handleUpdatePref(
    priority: string,
    field: 'notify_when_overdue' | 'channels',
    value: boolean | string[]
  ) {
    setUpdatingPrefPriority(priority)
    try {
      const updated = await updateNotificationPref(priority, { [field]: value })
      setNotifPrefs((prev) => prev.map((p) => (p.priority === priority ? updated : p)))
    } catch (err) {
      setNotifError(err instanceof Error ? err.message : 'Failed to update preference')
    } finally {
      setUpdatingPrefPriority(null)
    }
  }

  const totpDevices = devices.filter((d) => d.type === 'totp')
  const backupDevice = devices.find((d) => d.type === 'backup_codes')

  const changePasswordCard = (
    <div className="card mb-6">
      <h2 className="text-xl font-semibold mb-4">Change Password</h2>
      {passwordError && <div className="alert alert-error mb-4">{passwordError}</div>}
      {passwordSuccess && (
        <div className="alert alert-success mb-4">
          Password changed. All other sessions have been logged out.
        </div>
      )}
      <form onSubmit={handleChangePassword} className="space-y-4 max-w-sm">
        <div>
          <label className="label">Current Password</label>
          <input
            type="password"
            className="input"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="label">New Password</label>
          <input
            type="password"
            className="input"
            required
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <PasswordStrengthBar password={newPassword} />
          <p className="text-xs text-gray-400 mt-1">
            Minimum 15 characters — uppercase, lowercase, digit, and special character required.
          </p>
        </div>
        <div>
          <label className="label">Confirm New Password</label>
          <input
            type="password"
            className="input"
            required
            autoComplete="new-password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="label inline-flex items-center gap-1.5">
            Authenticator or backup code (if enrolled)
            <InfoHint label="What code is this?">
              The 6-digit code from your authenticator app, or a one-time backup
              code. Leave blank if you'll confirm with a passkey instead.
            </InfoHint>
          </label>
          <input
            type="text"
            inputMode="text"
            maxLength={8}
            className="input"
            placeholder="6-digit or backup code"
            value={passwordMfaCode}
            onChange={(e) => setPasswordMfaCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
          />
        </div>
        <button
          type="submit"
          disabled={savingPassword || !currentPassword || !newPassword}
          className="btn btn-primary"
        >
          {savingPassword ? 'Changing…' : 'Change Password'}
        </button>
        <PasskeyReauthButton
          onToken={doChangePassword}
          onError={setPasswordError}
          disabled={
            savingPassword ||
            !currentPassword ||
            newPassword.length < 15 ||
            newPassword !== confirmNewPassword
          }
          label="Change password with a passkey"
          busyLabel="Changing…"
        />
      </form>
    </div>
  )

  // Mandatory password change: the backend blocks every other endpoint with a
  // 403 until the password is changed, so render only the change-password form
  // (no nav to other panels that would just 403). Clearing the flag on success
  // refreshes the user and restores the full profile page.
  if (user?.force_password_change) {
    return (
      <div className="max-w-md mx-auto mt-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Change Your Password</h1>
        <div className="alert alert-warning mb-4">
          You must change your password before you can continue.
        </div>
        {changePasswordCard}
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">My Profile</h1>

      {/* Account Information */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold mb-4">Account Information</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Email</dt>
            <dd className="mt-1 text-sm text-gray-900">{user?.email}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Full Name</dt>
            <dd className="mt-1 text-sm text-gray-900">{user?.full_name || 'Not set'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Phone</dt>
            <dd className="mt-1 text-sm text-gray-900">{user?.phone || 'Not set'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Roles</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {user?.is_superuser ? (
                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800">
                  superuser
                </span>
              ) : (
                user?.roles?.map((r) => (
                  <span
                    key={r}
                    className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 mr-1"
                  >
                    {r}
                  </span>
                )) || 'None'
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Account Created</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {user?.created_at ? format(parseServerDate(user.created_at)!, 'PPP') : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Last Login</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {user?.last_login ? format(parseServerDate(user.last_login)!, 'PPpp') : 'Never'}
            </dd>
          </div>
        </dl>
      </div>

      {/* Update Profile */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold mb-4">Update Profile</h2>
        {profileError && <div className="alert alert-error mb-4">{profileError}</div>}
        {profileSuccess && (
          <div className="alert alert-success mb-4">Profile updated successfully.</div>
        )}
        <form onSubmit={handleSaveProfile} className="space-y-4 max-w-sm">
          <div>
            <label className="label">Full Name</label>
            <input
              type="text"
              className="input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Phone</label>
            <input
              type="tel"
              className="input"
              value={editPhone}
              onChange={(e) => setEditPhone(e.target.value)}
            />
          </div>
          <button type="submit" disabled={savingProfile} className="btn btn-primary">
            {savingProfile ? 'Saving…' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Change Password */}
      {changePasswordCard}

      {/* MFA Devices */}
      <div className="card mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold inline-flex items-center gap-2">
            Multi-Factor Authentication
            <InfoHint label="What is multi-factor authentication?">
              A second step at sign-in, on top of your password, so a stolen
              password isn't enough to get in. An <strong>authenticator app</strong>{' '}
              (Google Authenticator, Authy, 1Password, …) shows a fresh 6-digit code
              every 30 seconds — this is sometimes called TOTP. A passkey works as a
              second factor too.
            </InfoHint>
          </h2>
          <button className="btn btn-primary" onClick={() => setShowAddAuth(true)}>
            Add Authenticator
          </button>
        </div>

        {devicesError && <div className="alert alert-error mb-4">{devicesError}</div>}

        {devicesLoading ? (
          <div className="text-sm text-gray-500">Loading devices…</div>
        ) : totpDevices.length === 0 ? (
          <p className="text-sm text-gray-500 mb-2">No authenticator apps enrolled.</p>
        ) : (
          <div className="divide-y divide-gray-100 mb-4">
            {totpDevices.map((device) => (
              <div key={device.id} className="py-3">
                {renamingDeviceId === device.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      className="input flex-1 text-sm py-1"
                      value={renameDeviceLabel}
                      maxLength={50}
                      autoFocus
                      aria-label={`New label for ${device.label}`}
                      onChange={(e) => setRenameDeviceLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setRenamingDeviceId(null)
                        if (e.key === 'Enter') {
                          renameMFADevice(device.id, renameDeviceLabel).then(() => {
                            setDevices((prev) =>
                              prev.map((d) => d.id === device.id ? { ...d, label: renameDeviceLabel } : d)
                            )
                            setRenamingDeviceId(null)
                          })
                        }
                      }}
                    />
                    <button
                      className="text-xs text-primary-600 hover:text-primary-800 font-medium py-1 px-2"
                      aria-label={`Save new name for ${device.label}`}
                      onClick={() => {
                        renameMFADevice(device.id, renameDeviceLabel).then(() => {
                          setDevices((prev) =>
                            prev.map((d) => d.id === device.id ? { ...d, label: renameDeviceLabel } : d)
                          )
                          setRenamingDeviceId(null)
                        })
                      }}
                    >
                      Save
                    </button>
                    <button
                      className="text-xs text-gray-500 hover:text-gray-700 py-1 px-2"
                      aria-label="Cancel rename"
                      onClick={() => setRenamingDeviceId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{device.label}</p>
                      <p className="text-xs text-gray-500">
                        Used {device.use_count} time{device.use_count !== 1 ? 's' : ''}
                        {device.last_used_at &&
                          ` · Last used ${format(parseServerDate(device.last_used_at)!, 'MM/dd/yyyy')}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        className="text-xs text-gray-500 hover:text-gray-800 font-medium"
                        onClick={() => { setRenamingDeviceId(device.id); setRenameDeviceLabel(device.label) }}
                      >
                        Rename
                      </button>
                      <button
                        className="tap text-xs text-red-600 hover:text-red-800 font-medium"
                        onClick={() => setRemovingDevice(device)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Backup codes */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Backup Codes</p>
              <p className="text-xs text-gray-500">
                {backupDevice
                  ? `Generated ${format(parseServerDate(backupDevice.created_at)!, 'MM/dd/yyyy')}`
                  : 'Generated when you first enroll an authenticator'}
              </p>
            </div>
            {backupDevice && (
              <button
                className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                onClick={() => setShowRotateCodes(true)}
              >
                Rotate Codes
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Passkeys */}
      <div className="card mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold inline-flex items-center gap-2">
            Passkeys
            <InfoHint label="What is a passkey?">
              A passkey lets you sign in with your <strong>fingerprint, face, or
              screen lock</strong> (or a security key) instead of typing a password
              and a code. It's tied to this site, so it can't be phished, and it
              never leaves your device. Think of it as "sign in with my fingerprint."
            </InfoHint>
          </h2>
          {isPasskeySupported() && (
            <button className="btn btn-primary" onClick={() => setShowAddPasskey(true)}>
              Add Passkey
            </button>
          )}
        </div>

        {!isPasskeySupported() && (
          <p className="text-sm text-gray-500">
            Your browser does not support passkeys. Use a modern browser (Chrome, Safari, Firefox,
            Edge) on a device with biometrics or a security key.
          </p>
        )}

        {isPasskeySupported() && (
          <>
            <p className="text-sm text-gray-500 mb-4">
              Passkeys let you sign in with your fingerprint, face, or screen lock (or a
              security key) — no password or code to type. Each passkey is tied to this site,
              can't be phished, and never leaves your device.
            </p>

            {passkeysError && <div className="alert alert-error mb-4">{passkeysError}</div>}

            {passkeysLoading ? (
              <div className="text-sm text-gray-500">Loading passkeys…</div>
            ) : passkeys.length === 0 ? (
              <p className="text-sm text-gray-500">No passkeys enrolled.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {passkeys.map((pk) => (
                  <div key={pk.id} className="py-3">
                    {renamingPasskeyId === pk.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          className="input flex-1 text-sm py-1"
                          value={renamePasskeyLabel}
                          maxLength={50}
                          autoFocus
                          aria-label={`New label for ${pk.passkey_label}`}
                          onChange={(e) => setRenamePasskeyLabel(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setRenamingPasskeyId(null)
                            if (e.key === 'Enter') {
                              renamePasskey(pk.id, renamePasskeyLabel).then(() => {
                                setPasskeys((prev) =>
                                  prev.map((p) => p.id === pk.id ? { ...p, passkey_label: renamePasskeyLabel } : p)
                                )
                                setRenamingPasskeyId(null)
                              })
                            }
                          }}
                        />
                        <button
                          className="text-xs text-primary-600 hover:text-primary-800 font-medium py-1 px-2"
                          aria-label={`Save new name for ${pk.passkey_label}`}
                          onClick={() => {
                            renamePasskey(pk.id, renamePasskeyLabel).then(() => {
                              setPasskeys((prev) =>
                                prev.map((p) => p.id === pk.id ? { ...p, passkey_label: renamePasskeyLabel } : p)
                              )
                              setRenamingPasskeyId(null)
                            })
                          }}
                        >
                          Save
                        </button>
                        <button
                          className="text-xs text-gray-500 hover:text-gray-700 py-1 px-2"
                          aria-label="Cancel rename"
                          onClick={() => setRenamingPasskeyId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{pk.passkey_label}</p>
                          <p className="text-xs text-gray-500">
                            Added {format(parseServerDate(pk.created_at)!, 'MM/dd/yyyy')}
                            {pk.last_used_at &&
                              ` · Last used ${format(parseServerDate(pk.last_used_at)!, 'MM/dd/yyyy')}`}
                            {` · Used ${pk.use_count} time${pk.use_count !== 1 ? 's' : ''}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            className="text-xs text-gray-500 hover:text-gray-800 font-medium"
                            onClick={() => { setRenamingPasskeyId(pk.id); setRenamePasskeyLabel(pk.passkey_label) }}
                          >
                            Rename
                          </button>
                          <button
                            className="tap text-xs text-red-600 hover:text-red-800 font-medium"
                            onClick={() => setRemovingPasskey(pk)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Notification Settings */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold mb-4">Notification Settings</h2>

        {notifError && <div className="alert alert-error mb-4">{notifError}</div>}

        {notifLoading ? (
          <div className="text-sm text-gray-500">Loading notification settings…</div>
        ) : (
          <>
            {/* Notification Addresses */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-medium text-gray-700">Notification Addresses</h3>
                <button
                  className="btn btn-secondary text-sm"
                  onClick={() => {
                    setShowAddAddress(true)
                    setAddAddrError('')
                    setAddAddrAddress('')
                    setAddAddrLabel('')
                    setAddAddrChannel('email')
                  }}
                >
                  + Add Address
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Where to deliver notifications. You can add email-to-text addresses (e.g. 3035551234@tmomail.net) or additional emails.
              </p>
              {notifAddresses.length === 0 ? (
                <p className="text-sm text-gray-400">No notification addresses configured.</p>
              ) : (
                <div className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                  {notifAddresses.map((addr) => (
                    <div key={addr.id} className="flex items-center justify-between px-3 py-2 gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{addr.label}</p>
                        <p className="text-xs text-gray-500">{addr.address}</p>
                        <span className="inline-block text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 mt-0.5">
                          {addr.channel}
                        </span>
                      </div>
                      <button
                        className="tap flex-shrink-0 text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-40"
                        disabled={deletingAddrId === addr.id}
                        onClick={() => handleDeleteAddress(addr.id)}
                        title="Remove this address"
                      >
                        {deletingAddrId === addr.id ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Per-Priority Preferences */}
            <div>
              <h3 className="text-base font-medium text-gray-700 mb-3">
                Per-Priority Preferences
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Control when you receive notifications for tasks at each priority level.
              </p>
              <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
                {(['urgent', 'high', 'medium', 'low'] as const).map((priority) => {
                  const pref = notifPrefs.find((p) => p.priority === priority)
                  const priorityColors: Record<string, string> = {
                    urgent: 'bg-red-100 text-red-800',
                    high: 'bg-orange-100 text-orange-800',
                    medium: 'bg-yellow-100 text-yellow-800',
                    low: 'bg-gray-100 text-gray-600',
                  }
                  return (
                    <div key={priority} className="flex items-center gap-4 px-3 py-3">
                      <span
                        className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full w-16 text-center ${priorityColors[priority]}`}
                      >
                        {priority}
                      </span>
                      <div className="flex items-center gap-4 flex-1 flex-wrap">
                        <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pref?.channels.includes('email') ?? true}
                            disabled={updatingPrefPriority === priority}
                            onChange={(e) => {
                              if (!pref) return
                              const newChannels = e.target.checked
                                ? [...pref.channels.filter((c) => c !== 'email'), 'email']
                                : pref.channels.filter((c) => c !== 'email')
                              handleUpdatePref(priority, 'channels', newChannels)
                            }}
                            className="rounded"
                          />
                          Email
                        </label>
                        <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pref?.notify_when_overdue ?? false}
                            disabled={updatingPrefPriority === priority}
                            onChange={(e) =>
                              handleUpdatePref(priority, 'notify_when_overdue', e.target.checked)
                            }
                            className="rounded"
                          />
                          Notify when overdue
                        </label>
                      </div>
                      {updatingPrefPriority === priority && (
                        <span className="text-xs text-gray-400">Saving…</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Active Sessions */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Active Sessions</h2>
          <button
            className="btn btn-secondary text-sm"
            disabled={loggingOutAll || sessions.filter((s) => !s.is_current).length === 0}
            onClick={handleLogoutAll}
          >
            {loggingOutAll ? 'Logging out…' : 'Log Out All Other Sessions'}
          </button>
        </div>

        {sessionsError && <div className="alert alert-error mb-4">{sessionsError}</div>}
        {sessionsSuccess && <div className="alert alert-success mb-4">{sessionsSuccess}</div>}

        {sessionsLoading ? (
          <div className="text-sm text-gray-500">Loading sessions…</div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-gray-500">No active sessions found.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {sessions.map((session) => (
              <div key={session.id} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-900">
                      {session.ip_address || 'Unknown IP'}
                    </p>
                    {session.is_current && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                        This session
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{session.user_agent || 'Unknown browser'}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Started {format(parseServerDate(session.created_at)!, 'MM/dd/yyyy HH:mm')} · Last active{' '}
                    {format(parseServerDate(session.last_activity_at)!, 'MM/dd/yyyy HH:mm')}
                  </p>
                </div>
                {!session.is_current && (
                  <button
                    className="flex-shrink-0 text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-40 mt-0.5"
                    disabled={loggingOutSessionId === session.id}
                    onClick={() => handleLogoutSession(session.id)}
                    title="Log out this session"
                  >
                    {loggingOutSessionId === session.id ? 'Logging out…' : 'Log out'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trusted Devices */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Trusted Devices</h2>
        <p className="text-sm text-gray-500 mb-4">
          Devices marked as trusted can access the app from any IP address (bypasses IP
          restrictions). Devices are added when you check "Remember this device" at sign-in, or
          automatically after a passkey sign-in.
        </p>
        {trustedDevicesError && <div className="alert alert-error mb-4">{trustedDevicesError}</div>}
        {trustedDevicesLoading ? (
          <div className="text-sm text-gray-500">Loading trusted devices…</div>
        ) : trustedDevices.length === 0 ? (
          <p className="text-sm text-gray-500">No trusted devices registered.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {trustedDevices.map((device) => (
              <div key={device.id} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{device.device_name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Added {format(parseServerDate(device.created_at)!, 'MM/dd/yyyy')}
                    {device.created_from_ip ? ` from ${device.created_from_ip}` : ''} · Expires{' '}
                    {format(parseServerDate(device.expires_at)!, 'MM/dd/yyyy')} · Last seen{' '}
                    {format(parseServerDate(device.last_seen_at)!, 'MM/dd/yyyy HH:mm')}
                  </p>
                </div>
                <button
                  className="tap flex-shrink-0 text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-40"
                  disabled={revokingDeviceId === device.id}
                  onClick={() => handleRevokeDevice(device.id)}
                >
                  {revokingDeviceId === device.id ? 'Revoking…' : 'Revoke'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Login Policy */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Login Policy</h2>
        <p className="text-sm text-gray-500 mb-4">
          Control which IP addresses or devices can access your account. When set to{' '}
          <strong>Open</strong>, there are no restrictions.
        </p>
        {policyError && <div className="alert alert-error mb-4">{policyError}</div>}
        {policySuccess && <div className="alert alert-success mb-4">{policySuccess}</div>}
        {policyLoading ? (
          <div className="text-sm text-gray-500">Loading policy…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="label">Access mode</label>
              <select
                className="input"
                value={policyMode}
                onChange={(e) => setPolicyMode(e.target.value as typeof policyMode)}
              >
                <option value="open">Open — no IP or device restrictions</option>
                <option value="ip_only">IP only — must come from an allowed IP address</option>
                <option value="ip_or_trusted_device">IP or trusted device — allowed IP or remembered device</option>
              </select>
            </div>
            {policyMode !== 'open' && (
              <div>
                <label className="label">
                  Allowed IP ranges (CIDR notation, one per line)
                </label>
                <textarea
                  className="input font-mono text-sm"
                  rows={4}
                  placeholder={"64.187.71.99/32\n192.168.1.0/24"}
                  value={policyCidrs}
                  onChange={(e) => setPolicyCidrs(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Example: <code>64.187.71.99/32</code> for a single IP, <code>192.168.1.0/24</code> for a subnet.
                </p>
              </div>
            )}
            <div className="flex justify-end">
              <button
                className="btn btn-primary"
                disabled={savingPolicy}
                onClick={handleSavePolicy}
              >
                {savingPolicy ? 'Saving…' : 'Save Policy'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
        {activityError && <div className="alert alert-error mb-4">{activityError}</div>}
        {activityLoading ? (
          <div className="text-sm text-gray-500">Loading activity…</div>
        ) : activity.length === 0 ? (
          <p className="text-sm text-gray-500">No recent activity.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {activity.map((entry) => (
              <div key={entry.id} className={`py-2.5 flex items-start gap-3 ${!entry.success ? 'bg-red-50 -mx-4 px-4' : ''}`}>
                <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${entry.success ? 'bg-green-400' : 'bg-red-500'}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs font-mono text-gray-700">{entry.event_type}</span>
                    {entry.action && <span className="text-xs text-gray-500">{entry.action}</span>}
                    {!entry.success && <span className="text-xs text-red-600 font-medium">failed</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-400">
                      {format(parseServerDate(entry.timestamp)!, 'MM/dd/yyyy HH:mm')}
                    </span>
                    {entry.ip_address && (
                      <span className="text-xs text-gray-400 font-mono">{entry.ip_address}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddPasskey && (
        <AddPasskeyModal
          onClose={() => setShowAddPasskey(false)}
          onEnrolled={() => loadPasskeys()}
        />
      )}

      {removingPasskey && (
        <ReauthModal
          title="Remove Passkey"
          description={`Remove "${removingPasskey.passkey_label}"? You will need your current password to confirm.`}
          confirmLabel="Remove Passkey"
          onConfirm={handleRemovePasskey}
          onClose={() => setRemovingPasskey(null)}
        />
      )}

      {showAddAuth && (
        <AddAuthModal
          onClose={() => setShowAddAuth(false)}
          onEnrolled={() => loadDevices()}
        />
      )}

      {removingDevice && (
        <ReauthModal
          title="Remove MFA Device"
          description={`Remove "${removingDevice.label}"? You will need your current password to confirm.`}
          confirmLabel="Remove Device"
          onConfirm={handleRemoveDevice}
          onClose={() => setRemovingDevice(null)}
        />
      )}

      {showRotateCodes && (
        <ReauthModal
          title="Rotate Backup Codes"
          description="Generate new backup codes. Your existing codes will be invalidated immediately."
          confirmLabel="Generate New Codes"
          onConfirm={handleRotateCodes}
          onClose={() => setShowRotateCodes(false)}
        />
      )}

      {newCodes && (
        <BackupCodesDisplay codes={newCodes} onClose={() => setNewCodes(null)} />
      )}

      {/* Add Notification Address Modal */}
      {showAddAddress && (
        <Modal title="Add Notification Address" titleId="add-notif-addr-modal" onClose={() => setShowAddAddress(false)}>
          {addAddrError && <div className="alert alert-error mb-4">{addAddrError}</div>}
          <div className="space-y-4">
            <div>
              <label className="label">Channel</label>
              <select
                className="input"
                value={addAddrChannel}
                onChange={(e) => setAddAddrChannel(e.target.value)}
              >
                <option value="email">Email</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Use your carrier's email-to-text address (e.g. 3035551234@tmomail.net) to receive SMS via email.
              </p>
            </div>
            <div>
              <label className="label">Address</label>
              <input
                type="email"
                className="input"
                placeholder="email@example.com"
                value={addAddrAddress}
                onChange={(e) => setAddAddrAddress(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Label</label>
              <input
                type="text"
                className="input"
                placeholder='e.g. "Work Email" or "Email-to-Text"'
                value={addAddrLabel}
                onChange={(e) => setAddAddrLabel(e.target.value)}
              />
            </div>
          </div>
          <ModalFooter>
            <button className="btn btn-secondary" onClick={() => setShowAddAddress(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={addAddrSubmitting || !addAddrAddress.trim() || !addAddrLabel.trim()}
              onClick={handleAddAddress}
            >
              {addAddrSubmitting ? 'Adding…' : 'Add Address'}
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}
